import { useState, useEffect, useContext } from 'react';
import { Translate } from '@/components';
import { useLocation } from 'react-router-dom';
import MuiBottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { APP_ROUTES } from '@/config/constants';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import styles from './BottomNavigation.styles';
import AuthenticationContext from '@/context/AuthenticationContext';
import { openLoginDrawerVar } from '@/graphql/state';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import { useNavigateWithTracking } from '@/hooks';

const options = [
  {
    label: 'Home',
    icon: <HomeOutlinedIcon />,
    route: APP_ROUTES.home,
  },
  {
    label: 'The Jar',
    icon: <SearchOutlinedIcon />,
    route: APP_ROUTES.jar,
  },
  // {
  //   label: 'Upcoming',
  //   icon: <CalendarTodayOutlinedIcon />,
  //   route: APP_ROUTES.upcoming,
  // },
  {
    label: 'Saved',
    icon: <StarOutlineIcon />,
    route: APP_ROUTES.saved,
  },
  {
    label: 'Account',
    icon: <AccountCircleOutlinedIcon />,
    route: APP_ROUTES.account,
  },
];

export const BottomNavigation: React.FC = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const navigateWithTracking = useNavigateWithTracking();
  const { pathname } = useLocation();
  const { track, trackEvent } = useAnalytics();
  const [value, setValue] = useState(() => options.findIndex(o => o.route === pathname));
  useEffect(() => {
    const normalizedPath =
      pathname.startsWith(APP_ROUTES.explore) || pathname.startsWith('/home')
        ? APP_ROUTES.home
        : `/${pathname.split('/')[1]}`;

    setValue(options.findIndex(o => o.route === normalizedPath));
  }, [pathname]);

  return (
    <footer className={styles.container}>
      <MuiBottomNavigation
        showLabels
        className={styles.navigation}
        value={value}
        onChange={(event, newValue) => {
          if (isAuthenticated) {
            setValue(newValue);
            const trackingFrom = {
              event_detail_from: options[value].label.toLowerCase(),
              event_from: TrackingEvents.browseContent,
              stage_from: TrackingStage.discovery,
            };
            navigateWithTracking(options[newValue].route, trackingFrom);

            if (
              options[newValue].route === APP_ROUTES.home ||
              options[newValue].route === APP_ROUTES.account
            ) {
              trackEvent({
                event: TrackingEvents.browseContent,
                event_detail: options[newValue].label.toLowerCase(),
                stage: TrackingStage.discovery,
                ...trackingFrom,
              });
            }

            track({
              action: GenericTrackTypes.BUTTON_PRESSED,
              category: 'bottom_navigation',
              label: options[newValue].label,
            });
          } else {
            openLoginDrawerVar(true);
          }
        }}
        component="nav"
        aria-label="Main Navigation"
      >
        {options.map(({ label, icon }, index) => (
          <BottomNavigationAction
            key={label}
            className={styles.action}
            label={<Translate>{label}</Translate>}
            icon={icon}
            data-testid={`${label.toLowerCase()}Button`}
            aria-current={value === index ? 'page' : undefined}
          />
        ))}
      </MuiBottomNavigation>
    </footer>
  );
};
